import React, { useState, useEffect } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Box,
  IconButton, Button, Dialog, DialogTitle, DialogContent
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Signal } from "../../models/signal.ts";
import { Relais } from "../../models/signalUsers/relais.ts";
import EditRelaisForm from "./EditRelaisForm.tsx";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { v4 as uuidv4 } from 'uuid';
import { useInvalidRowStyle } from "../../utils/getInvalidRowStyle.ts";

interface Props {
  data: Relais[];
  onChange: (items: Relais[]) => void;
  signalOptions: Signal[];
}

const emptySignal: Signal = {
  name: "empty",
  net: "empty",
  id: ""
}

const emptyRelais: Relais = {
  high_signal: emptySignal,
  low_signal: emptySignal,
  id: "",
  index: 0
};

const RelaisTable: React.FC<Props> = ({ data, onChange, signalOptions }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogConentIsValid, setDialogConentIsValid] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editRelais, setEditReleais] = useState<Relais>(emptyRelais);
  const getRowStyle = useInvalidRowStyle();

  const reindex = (items: Relais[]): Relais[] =>
    items
      .slice()
      .sort((a, b) => a.index - b.index)
      .map((item, i) => ({ ...item, index: i }));

  useEffect(() => {
    const validIds = new Set(signalOptions.map(s => s.id));
    const cleanedRelais = data.filter(item =>
      validIds.has(item.high_signal.id) && validIds.has(item.low_signal.id)
    );
    if (cleanedRelais.length !== data.length) {
      onChange(cleanedRelais);
    }
  }, [signalOptions, data, onChange]);

  const handleSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = editRelais;
    } else {
      updated.push(editRelais);
    }
    onChange(updated);
    setDialogConentIsValid(false);
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDialogConentIsValid(false);
    setEditIndex(null);
    setEditReleais(emptyRelais);
  };
  
  const handleAdd = () => {
    setEditIndex(null); 
    const edit = emptyRelais;
    edit.id = uuidv4();
    edit.index = data.length;
    setEditReleais(edit);
    setOpenDialog(true); 
  };

  return (
    <>
      {data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pin 1</TableCell>
              <TableCell>Pin 2</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const duplicateIndices = new Set<number>();
              const seen = new Map<string, number>();

              data.forEach((item, index) => {
                const key = [item.high_signal.id, item.low_signal.id].sort().join("|");
                if (seen.has(key)) {
                  duplicateIndices.add(index);
                  duplicateIndices.add(seen.get(key)!);
                } else {
                  seen.set(key, index);
                }
              });
              return (
                <TableRow
                  key={item.index}
                  sx={getRowStyle(duplicateIndices.has(index))}
                >
                  <TableCell>{getSignalLabelFromList(item.high_signal.id, signalOptions)}</TableCell>
                  <TableCell>{getSignalLabelFromList(item.low_signal.id, signalOptions)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => { setEditReleais(item); setEditIndex(index); setOpenDialog(true); }}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onChange(reindex(data.filter((_, i) => i !== index)))}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      
      <Box mt={2} />
      <Box mt={2} display="flex" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { handleAdd() }}
          disabled={signalOptions.length===0}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Relais bearbeiten</DialogTitle>
        <DialogContent>
          <EditRelaisForm data={editRelais} onChange={setEditReleais} setIsValid={setDialogConentIsValid} signalOptions={signalOptions} />
          
          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dialogConentIsValid}
              sx={{ mt: 2 }}
            >
              Speichern
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Abbrechen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RelaisTable;
